import { useEffect, useState } from "react";
import type { DoczModule } from "@norkron/web-docz";
import { sharedDoczModule } from "./doczHandle";

/**
 * `useDoczModule` — resolves the shared, memoized `docz.wasm` module once on
 * mount (rich-comments §4.1). The rich comment composer (`<CommentEditor>`) and
 * every rich thread body (`renderFragmentToHtml` → `sanitizeCommentHtml`) need
 * the loaded `DoczModule`; it is the SAME cached singleton the edit path boots
 * (`sharedDoczModule()`), so comments never trigger a duplicate load.
 *
 * While `null` (pre-hydration): the composer shows a skeleton and thread bodies
 * render plain text (`fragmentText`, XSS-free) — the rich HTML swaps in on
 * resolve with no layout jump and no unsanitized content before the sanitizer is
 * available.
 */
export function useDoczModule(): DoczModule | null {
  const [module, setModule] = useState<DoczModule | null>(null);
  useEffect(() => {
    let live = true;
    void sharedDoczModule()
      .then((m) => {
        if (live) setModule(m);
      })
      .catch(() => {
        // A load failure degrades gracefully: the composer stays a skeleton and
        // bodies stay plain text; a comment surface never throws.
      });
    return () => {
      live = false;
    };
  }, []);
  return module;
}
